// InfoPage.tsx
import React, { useState, ChangeEvent, useContext, useCallback, useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getInvoiceFormCheck } from '../../util/apiUtil';
import InputField from '../InputField';
import ConfirmButton from '../ConfirmButton';
import { GlobalStateContext } from '../../provider/GlobalStateProvider';
import { convertServerMessageToClient, isNullOrEmpty } from '../../util';
import { logoBanner } from '../../assets/images';
import { aesEncrypt } from '../../util/aes';
import moment from 'moment';

interface FormData {
    receiptnumber: string;
    tel: string;
    [key: string]: string;
}

const InfoPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const defaultData = process.env.NODE_ENV === 'production' ? { receiptnumber: '', tel: '' } :
    { receiptnumber: 'GZNBA823537', tel: '13826301290' };
    const [formData, setFormData] = useState<FormData>(defaultData);
    const [submitError, setSubmitError] = useState<string>('');
    const { setGlobalState } = useContext(GlobalStateContext);
    const { receiptnumber, tel } = formData;
    const [missingFields, setMissingFields] = useState<string[]>([]);

    const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormData(prevState => ({ ...prevState, [event.target.name]: event.target.value }));
        setSubmitError(''); // Reset submitError when the input field value changes
    }, []);

    const handleConfirm = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        let combinedIdAndPhone = `${receiptnumber}:${tel}`;
        let missingFields = [];
        if (isNullOrEmpty(receiptnumber)) {
            missingFields.push('receiptnumber');
        }
        if (isNullOrEmpty(tel)) {
            missingFields.push('tel');
        }
        if (missingFields.length > 0) {
            // setSubmitError(error);
            setMissingFields(missingFields || []);
            return;
        }
        setSubmitError(''); // Reset submitError when the user clicks the Confirm button
        const response = await getInvoiceFormCheck(combinedIdAndPhone);
        if (response?.error || response?.Result === 'false') {
            const error = response?.error || convertServerMessageToClient(response?.Message, t);
            setSubmitError(error);
            setMissingFields(missingFields || []);
        } else {
            if (response && Array.isArray(response)) {
                const receipt = response?.find((item: any) => item?.RECEIPTNUMBER === receiptnumber);
                if (!receipt) {
                    setSubmitError(t('error.receiptNotFound'));
                    return;
                }
                console.log('receipt', receipt)
                const RECEIPTDATE = receipt?.RECEIPTDATE;

                // Parse the receipt date
                const receiptDate = moment(RECEIPTDATE, 'YYYY-MM-DD');

                // Get today's date
                const today = moment();

                // Check if the receipt date is within today or yesterday
                const isValidDate = receiptDate.isSame(today, 'day') || receiptDate.isSame(moment(today).subtract(1, 'days'), 'day');

                if (!isValidDate) {
                    setSubmitError(t('error.receiptDateExpired'));
                    return;
                }

                setGlobalState(prevState => ({
                    ...prevState,
                    receiptnumber,
                    tel,
                    invoiceDetail: receipt
                }));

                const id = aesEncrypt(`${receiptnumber}:${tel}`);
                navigate(`/invoiceForm/${id}`);
            }
        }
    }, [navigate, receiptnumber, setGlobalState, t, tel]);

    const renderInputField = (name: string, labelKey: string) => (
        <InputField
            name={name}
            value={formData?.[name] || ''}
            onChange={handleInputChange}
            label={t(labelKey)}
            typographySx={{ flex: 1, marginBottom: '1rem' }}
            containerSx={{ width: '90%', marginBottom: '1rem' }}
            missingFields={missingFields}
            helperText={(field: string) => t('error.pleaseFillInRequiredFields',
                { field: t(`invoiceForm.inputField.title.${field}`) })}
        />
    );

    useEffect(() => {
        setGlobalState(prevState => ({
            ...prevState,
            tel: '',
            receiptnumber: '',
        }));
    }, [])

    return (
        <Grid
            container
            component="form"
            noValidate
            autoComplete="off"
            direction={'column'}
            justifyContent={'center'}
            alignContent={'center'}
            alignItems={'center'}
            sx={{ flex: 1, overflow: 'hidden' }}
        >
            <Box sx={{ marginBottom: '0.5rem' }}>
                <img src={logoBanner} alt="logoBanner" />
            </Box>
            {renderInputField('receiptnumber', 'invoiceForm.inputField.title.pleaseEnterReceiptnumber')}
            {renderInputField('tel', 'invoiceForm.inputField.title.pleaseEnterTel')}
            {submitError && (
                <Typography variant='body1' sx={{ color: 'var(--danger)', marginBottom: '1rem !important' }}>
                    {submitError}
                </Typography>
            )}
            <ConfirmButton onClick={handleConfirm} />
        </Grid>
    );
}

export default InfoPage;